/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { useState, useEffect, useCallback } from 'preact/compat';
import { events } from '@adobe-commerce/event-bus';
import { removeQuoteTemplateItems } from '@/quote-management/api/removeQuoteTemplateItems';
import { CartItemModel } from '@/quote-management/data/models/negotiable-quote-model';

const NOTIFICATION_AUTO_DISMISS_DELAY = 3000;

export interface UseRemoveTemplateItemsReturn {
  handleRemoveItems: (items: CartItemModel[]) => void;
  handleConfirmRemove: () => Promise<void>;
  handleCancelRemove: () => void;
  isRemoveModalOpen: boolean;
  itemsToRemove: CartItemModel[];
  isRemoving: boolean;
  removeNotificationState: {
    type: 'success' | 'error' | null;
    message: string;
  };
}

export interface UseRemoveTemplateItemsParams {
  templateId?: string;
  onRemoveModalStateChange?: (isOpen: boolean) => void;
  removeSuccessMessage: string;
  removeErrorMessage: string;
}

/**
 * Custom hook to manage removing items from a quote template
 */
export const useRemoveTemplateItems = (
  params: UseRemoveTemplateItemsParams
): UseRemoveTemplateItemsReturn => {
  const {
    templateId,
    onRemoveModalStateChange,
    removeSuccessMessage,
    removeErrorMessage,
  } = params;

  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [itemsToRemove, setItemsToRemove] = useState<CartItemModel[]>([]);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeNotificationState, setRemoveNotificationState] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  // Listen for quote template data event (successful removal)
  useEffect(() => {
    const templateDataEvent = events.on(
      'quote-management/quote-template-data',
      () => {
        setItemsToRemove([]);
        setIsRemoving(false);
        setRemoveNotificationState({
          type: 'success',
          message: removeSuccessMessage,
        });
        // Auto-close success notification after delay
        setTimeout(() => {
          setIsRemoveModalOpen(false);
          setRemoveNotificationState({ type: null, message: '' });
          onRemoveModalStateChange?.(false);
        }, NOTIFICATION_AUTO_DISMISS_DELAY);
      }
    );
    return () => templateDataEvent?.off();
  }, [removeSuccessMessage, onRemoveModalStateChange]);

  // Unified handler for removing items (single or multiple)
  const handleRemoveItems = useCallback((items: CartItemModel[]) => {
    if (!items || items.length === 0) {
      return;
    }
    setItemsToRemove(items);
    setRemoveNotificationState({ type: null, message: '' });
    setIsRemoveModalOpen(true);
  }, []);

  const handleConfirmRemove = useCallback(async () => {
    if (!templateId || itemsToRemove.length === 0) {
      return;
    }

    const uidsToRemove = itemsToRemove.map((item) => item.uid).filter(Boolean) as string[];

    if (uidsToRemove.length === 0) {
      return;
    }

    setIsRemoving(true);
    setRemoveNotificationState({ type: null, message: '' });

    try {
      await removeQuoteTemplateItems({
        templateId,
        itemUids: uidsToRemove,
      });
      // Success is handled by the event listener
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : removeErrorMessage;
      setRemoveNotificationState({
        type: 'error',
        message: errorMessage,
      });
      setIsRemoving(false);
    }
  }, [templateId, itemsToRemove, removeErrorMessage]);

  const handleCancelRemove = useCallback(() => {
    setIsRemoveModalOpen(false);
    setRemoveNotificationState({ type: null, message: '' });
    onRemoveModalStateChange?.(false);
    setItemsToRemove([]);
  }, [onRemoveModalStateChange]);

  return {
    handleRemoveItems,
    handleConfirmRemove,
    handleCancelRemove,
    isRemoveModalOpen,
    itemsToRemove,
    isRemoving,
    removeNotificationState,
  };
};

